import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ExternalLink,
  Github,
  Tag,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useTheme } from "../hooks/useTheme";

const ProjectModal = ({ project, onClose }) => {
  const { isDarkMode } = useTheme();
  const [imgIndex, setImgIndex] = useState(0);

  // Reset gallery index whenever a new project opens
  useEffect(() => {
    setImgIndex(0);
  }, [project?.id]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (!project) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [project]);

  const images = project?.images ?? [];
  const hasImages = images.length > 0;
  const hasMultiple = images.length > 1;

  const prev = (e) => {
    e.stopPropagation();
    setImgIndex((i) => (i - 1 + images.length) % images.length);
  };
  const next = (e) => {
    e.stopPropagation();
    setImgIndex((i) => (i + 1) % images.length);
  };

  return (
    <AnimatePresence>
      {project && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[2000] bg-graphite-void/80 backdrop-blur-md"
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Centering wrapper — this is what actually centers the panel */}
          <div className="fixed inset-0 z-[2001] flex items-center justify-center p-2 sm:p-4 pointer-events-none">
            <motion.div
              key="modal"
              initial={{ opacity: 0, scale: 0.94, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 24 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              role="dialog"
              aria-modal="true"
              aria-label={project.title}
              onClick={(e) => e.stopPropagation()}
              className={`pointer-events-auto relative w-full max-w-5xl max-h-[95vh] rounded-2xl overflow-hidden border flex flex-col ${
                isDarkMode
                  ? "bg-graphite-darkest border-graphite-medium/30"
                  : "bg-white border-gray-200"
              } shadow-2xl shadow-graphite-void`}
            >
              {/* Close button */}
              <button
                onClick={onClose}
                className={`absolute top-4 right-4 z-20 p-2 rounded-full transition-colors duration-200 ${
                  isDarkMode
                    ? "bg-graphite-dark/80 hover:bg-graphite-charcoal text-graphite-silver hover:text-white"
                    : "bg-white/80 hover:bg-gray-100 text-gray-600 hover:text-gray-900"
                } backdrop-blur-sm`}
                aria-label="Close modal"
              >
                <X size={18} />
              </button>

              {/* Scrollable content */}
              <div className="overflow-y-auto flex-1">
                {/* ── Image gallery ── */}
                <div className="flex flex-col shrink-0 overflow-hidden rounded-xl mx-6 mt-6 mb-4 bg-graphite-dark">
                  {/* Image + nav controls */}
                  <div className="relative h-72 sm:h-96">
                    <AnimatePresence mode="wait">
                      {hasImages ? (
                        <motion.img
                          key={images[imgIndex]}
                          src={images[imgIndex]}
                          alt={`${project.title} screenshot ${imgIndex + 1}`}
                          initial={{ opacity: 0, x: 40 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -40 }}
                          transition={{ duration: 0.25 }}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <motion.div
                          key="gradient"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className={`w-full h-full bg-gradient-to-br ${project.gradient}`}
                        >
                          <div
                            className="absolute inset-0 opacity-20"
                            style={{
                              backgroundImage:
                                "repeating-linear-gradient(127deg, transparent 0px, rgba(255,255,255,0.04) 0.5px, rgba(255,255,255,0.08) 1px, transparent 2px, transparent 8px)",
                            }}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Prev / Next arrows */}
                    {hasMultiple && (
                      <>
                        <button
                          onClick={prev}
                          className="absolute left-3 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-graphite-void/60 hover:bg-graphite-void/80 text-white backdrop-blur-sm transition-colors"
                          aria-label="Previous image"
                        >
                          <ChevronLeft size={18} />
                        </button>
                        <button
                          onClick={next}
                          className="absolute right-3 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-graphite-void/60 hover:bg-graphite-void/80 text-white backdrop-blur-sm transition-colors"
                          aria-label="Next image"
                        >
                          <ChevronRight size={18} />
                        </button>
                      </>
                    )}

                    {/* Dot indicators */}
                    {hasMultiple && (
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                        {images.map((_, i) => (
                          <button
                            key={i}
                            onClick={(e) => {
                              e.stopPropagation();
                              setImgIndex(i);
                            }}
                            aria-label={`Go to image ${i + 1}`}
                            className={`rounded-full transition-all duration-200 ${
                              i === imgIndex
                                ? "w-5 h-1.5 bg-white"
                                : "w-1.5 h-1.5 bg-white/40 hover:bg-white/70"
                            }`}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                </div>

                {/* ── Body ── */}
                <div className="px-6 pb-6 pt-2">
                  {/* Category + title */}
                  <div className="mb-4">
                    <span
                      className={`inline-block text-xs font-semibold uppercase tracking-widest mb-2 ${
                        isDarkMode
                          ? "text-graphite-silver"
                          : "text-graphite-slate"
                      }`}
                    >
                      {project.category}
                    </span>
                    <h2
                      className={`text-2xl sm:text-3xl font-bold ${
                        isDarkMode ? "text-white" : "text-graphite-darkest"
                      }`}
                    >
                      {project.title}
                    </h2>
                  </div>

                  {/* Description */}
                  <p
                    className={`text-base leading-relaxed mb-6 ${
                      isDarkMode
                        ? "text-graphite-silver"
                        : "text-graphite-medium"
                    }`}
                  >
                    {project.description}
                  </p>

                  {/* Tech stack */}
                  <div className="mb-6">
                    <h3
                      className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-widest mb-3 ${
                        isDarkMode
                          ? "text-graphite-light"
                          : "text-graphite-slate"
                      }`}
                    >
                      <Tag size={12} />
                      Tech Stack
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {project.tags.map((tag) => (
                        <span
                          key={tag}
                          className={`text-sm px-3 py-1 rounded-full font-medium border ${
                            isDarkMode
                              ? "bg-graphite-dark text-graphite-platinum border-graphite-medium/40"
                              : "bg-gray-100 text-graphite-medium border-gray-200"
                          }`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Links */}
                  {(project.links.live || project.links.github) && (
                    <div
                      className={`flex flex-wrap gap-3 pt-5 border-t ${
                        isDarkMode
                          ? "border-graphite-medium/20"
                          : "border-gray-200"
                      }`}
                    >
                      {project.links.live && (
                        <a
                          href={project.links.live}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                            isDarkMode
                              ? "bg-graphite-steel text-white hover:bg-graphite-medium"
                              : "bg-graphite-darkest text-white hover:bg-graphite-charcoal"
                          }`}
                        >
                          <ExternalLink size={15} />
                          Live Site
                        </a>
                      )}
                      {project.links.github && (
                        <a
                          href={project.links.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold border transition-all duration-200 ${
                            isDarkMode
                              ? "border-graphite-medium/40 text-graphite-silver hover:border-graphite-silver hover:text-white"
                              : "border-gray-300 text-graphite-medium hover:border-gray-500 hover:text-graphite-darkest"
                          }`}
                        >
                          <Github size={15} />
                          GitHub
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ProjectModal;
